import { useQuery } from "@tanstack/react-query";
import { Control, FieldValues, Path, useController } from "react-hook-form";
import styled, { css } from "styled-components";

import { Label } from "@/components/Forms";
import { TestingChannelResource } from "@/types/resources";
import { api } from "@/utils/api";
import { Image } from "./TestingChannelsTableRow";
import NetworkIcon from "@/components/NetworkIcon";
import Text from "@/components/Text";
import { numFormatter } from "@/utils/helpers";
import ValidationMessage from "@/components/Forms/ValidationMessage";
import ImgPlaceholder from "@/assets/icons/img-placeholder.svg";

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
`;

const Card = styled.button<{ $isActive: boolean }>`
  text-align: left;
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.grayscaleLight};
  border-radius: 4px;
  transition: 0.1s ease-out border;
  padding: 14px;
  display: flex;
  align-items: flex-start;
  cursor: pointer;
  &:hover {
    border-color: ${({ theme }) => theme.colors.gray};
  }

  ${({ theme, $isActive }) =>
    $isActive &&
    css`
      border-color: ${theme.colors.purple} !important;
    `}
`;

const StyledLabel = styled(Label)`
  font-size: 11px;
  line-height: 15px;
`;

const ImageWrap = styled.div`
  position: relative;
  margin-right: 8px;
  svg {
    position: absolute;
    top: -2px;
    left: -2px;
  }
`;

const ChannelUrl = styled(Text)`
  overflow-x: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const Description = styled.div`
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Subscribers = styled(Text)`
  margin-top: 8px;
`;

const Box = styled.div`
  padding: 16px 24px 32px;
`;

interface Props<T extends FieldValues> {
  control: Control<T>;
  name: Path<T>;
  label: string;
}

const TestingChannelsSelectionField = <T extends FieldValues>({
  control,
  name,
  label,
}: Props<T>) => {
  const { data: channels } = useQuery(
    ["testing_channels"],
    () => api.get<never, TestingChannelResource[]>("/v1/testing-channels"),
    {
      staleTime: 60 * 1000,
      select: (channels) => channels.filter((c) => c.authorized),
    }
  );

  const {
    field: { onChange, value },
    fieldState: { error },
  } = useController({
    name,
    control,
  });

  const onChannelClick = (channelId: number) => {
    if (value.includes(channelId)) {
      return onChange(value.filter((c: number) => c !== channelId));
    }

    onChange([...value, channelId]);
  };

  return (
    <Box>
      <StyledLabel>{label}</StyledLabel>
      <Grid>
        {channels?.map((channel) => (
          <Card
            $isActive={value.includes(channel.id)}
            key={channel.id}
            type="button"
            onClick={() => onChannelClick(channel.id)}
          >
            <ImageWrap>
              <Image
                src={channel.thumbnail || ImgPlaceholder}
                alt={channel.name + " thumbnail"}
              />
              <NetworkIcon type={channel.network} />
            </ImageWrap>
            <Description>
              <Text $type="smallBold">{channel.name}</Text>
              <ChannelUrl $type="extraSmall" $color="gray" as="span">
                {channel.url}
              </ChannelUrl>
              <Subscribers $type="extraSmall">
                {!!channel.subscribers
                  ? `${numFormatter(channel.subscribers, 1)} subscribers`
                  : "-"}
              </Subscribers>
            </Description>
          </Card>
        ))}
      </Grid>
      {!!error && <ValidationMessage>{error.message}</ValidationMessage>}
    </Box>
  );
};

export default TestingChannelsSelectionField;
